// Google tag (gtag.js) for the Google Ads account, loaded for US visitors only.
//
// Conversions are measured WITHOUT the tag (gclid on the server-side lead
// event, see docs/google-ads-conversions.md). The tag is loaded on top of that
// for what click-import cannot do: the conversion linker cookie (_gcl_aw, so a
// conversion still attributes when the gclid never reaches the form, e.g. a
// return visit) and remarketing audiences.
//
// Two rules keep its cost near zero:
//  - Region gate, decided on the server per request. EEA/UK visitors never get
//    the tag at all, so no consent banner / Consent Mode is needed; only
//    visitors in TAG_REGIONS receive the snippet in the HTML.
//  - Late load, decided in the browser: the snippet waits for window load and
//    then an idle slot before injecting gtag.js (~155 KB gzipped plus 6+ extra
//    hosts), so LCP and the main thread during paint are untouched.
//
// The region comes from the external HTTPS load balancer, which stamps
// X-Client-Region:{client_region} (ISO 3166-1 alpha-2) on every request to the
// backend service (gcloud compute backend-services update
// pine59-landing-backend --custom-request-header). Direct *.run.app URLs
// (PR previews, local dev) carry no such header, so there the tag is off
// unless the ?region= override is used; see wantsGoogleTag.

export const GOOGLE_ADS_TAG_ID = "AW-18465576595"

const REGION_HEADER = "x-client-region"
const REGION_OVERRIDE_PARAM = "region"
const TAG_REGIONS = new Set(["US"])

// Country code the request came from, upper-cased, or null when unknown.
// The query-string override exists for testing previews and local dev (which
// never see the LB header); on production it only lets someone opt
// themselves INTO the tag, which is harmless.
export function clientRegion(request: Request): string | null {
  const override = new URL(request.url).searchParams.get(REGION_OVERRIDE_PARAM)
  const raw = override || request.headers.get(REGION_HEADER)
  if (!raw) return null
  const code = raw.trim().toUpperCase()
  return /^[A-Z]{2}$/.test(code) ? code : null
}

export function wantsGoogleTag(request: Request): boolean {
  const region = clientRegion(request)
  return region !== null && TAG_REGIONS.has(region)
}

// Inline bootstrap, rendered into <body> only when wantsGoogleTag() is true.
// Mirrors Google's snippet (dataLayer + gtag("js") + gtag("config")) but
// defers the script injection to after load + idle. gtag.js drains the
// dataLayer queue when it arrives, so the early pushes are not lost.
export function googleTagSnippet(tagId: string): string {
  const id = JSON.stringify(tagId)
  return `(function(){var id=${id};
window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments)}window.gtag=gtag;
gtag("js",new Date());gtag("config",id);
function go(){var s=document.createElement("script");s.async=true;s.src="https://www.googletagmanager.com/gtag/js?id="+id;document.head.appendChild(s)}
function idle(){if("requestIdleCallback" in window)requestIdleCallback(go,{timeout:4000});else setTimeout(go,0)}
if(document.readyState==="complete")idle();else addEventListener("load",idle);
})();`
}
